import { AvatarPanel } from '@/components/AvatarPanel';
import { MeasurementDifferences } from '@/components/MeasurementDifferences';
import { useSavedMatches } from '@/hooks/useSavedMatches';
import type { Audience, Category, ComparisonResult } from '@/types/domain';
import type { ComparisonSource } from '@/types/history';

interface Props {
  audience: Audience;
  category: Category;
  result: ComparisonResult | null;
  /** When provided (alongside a result), enables "Save this match" — see Fit History / Saved Matches. */
  source?: ComparisonSource | null;
  /** Skips rendering the avatar — used on the Dashboard, which renders one shared avatar in its own "Fit Profile" card instead of one per detail panel. */
  hideAvatar?: boolean;
  /** Drops this panel's own card chrome (border/shadow/padding) — used on the Dashboard, where it's nested inside the "Fit Profile" card and a second nested card would double the padding and look boxed-in-a-box. */
  bare?: boolean;
}

/**
 * The single source of truth for "what am I looking at right now": avatar
 * shading, recommended size, written explanation, and measurement
 * differences, all read off one selected ComparisonResult so they can never
 * drift out of sync with each other when the user picks a different card.
 */
export function FitDetailPanel({ audience, category, result, source, hideAvatar = false, bare = false }: Props) {
  const { save, remove, savedMatches, isSaved } = useSavedMatches();
  const alreadySaved = result && source ? isSaved(result.targetBrandSlug, source.sourceDescription) : false;

  function toggleSave() {
    if (!result || !source) return;
    if (alreadySaved) {
      const existing = savedMatches.find(
        (m) => m.result.targetBrandSlug === result.targetBrandSlug && m.sourceDescription === source.sourceDescription
      );
      if (existing) remove(existing.id);
    } else {
      save(source, result);
    }
  }

  return (
    <div className="space-y-4">
      {!hideAvatar && <AvatarPanel audience={audience} zones={result?.zones} />}
      {result ? (
        <div className={bare ? 'space-y-4' : 'space-y-3 rounded-2xl border border-stone-200 bg-white p-4 shadow-card'}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-medium uppercase tracking-wide text-muted">Recommended size</p>
              <p className="text-xl font-bold text-ink">
                {result.targetBrandName} {result.recommendedSize}
              </p>
            </div>
            {source && (
              <button
                type="button"
                onClick={toggleSave}
                aria-pressed={alreadySaved}
                className={`shrink-0 rounded-full border px-4 py-2 text-sm font-semibold ${
                  alreadySaved ? 'border-brand bg-brand text-ink' : 'border-stone-200 text-muted hover:border-brand/40'
                }`}
              >
                {alreadySaved ? 'Saved ✓' : 'Save match'}
              </button>
            )}
          </div>
          <p className="text-base text-ink/80">{result.fitDescription}</p>
          <MeasurementDifferences category={category} delta={result.delta} zones={result.zones} />
        </div>
      ) : (
        <p className="rounded-2xl border border-dashed border-stone-200 p-4 text-center text-sm text-muted">
          Select a brand above to see its fit map here.
        </p>
      )}
    </div>
  );
}
